import { Field, Input, Select } from '@bb/ui';
import type { IssueLookup } from './pushChecks.ts';
import type { PushButtonAction } from './pushDraft.ts';
import { FieldIssueList } from './PushIssues.tsx';

const KINDS: Array<{ value: PushButtonAction['kind']; label: string }> = [
  { value: 'open_app', label: 'Open the app' },
  { value: 'deep_link', label: 'Deep link' },
  { value: 'web_url', label: 'Web URL' },
];
const DISMISS = { value: 'dismiss' as const, label: 'Dismiss the notification' };
const OPEN_IN = [
  { value: 'external', label: 'In the browser' },
  { value: 'in_app', label: 'In the app' },
];

/** The same link when the kind changes between deep link and web URL; nothing to keep for the others. */
function switchKind(current: PushButtonAction, kind: PushButtonAction['kind']): PushButtonAction {
  const uri = 'uri' in current ? current.uri : '';
  if (kind === 'deep_link') return { kind, uri };
  if (kind === 'web_url') return { kind, uri, openIn: current.kind === 'web_url' ? (current.openIn ?? 'external') : 'external' };
  return { kind };
}

export interface ActionEditorProps {
  /** Label of the kind select, e.g. «On-click behavior» or «Button 1 action». */
  label: string;
  value: PushButtonAction;
  onChange: (next: PushButtonAction) => void;
  /** Buttons may only close the notification. */
  allowDismiss?: boolean;
  readOnly?: boolean;
  /** Issues of this action, looked up under `path` (`action`, `actionOverrides.ios`, `androidButtons[0].action`). */
  issues?: IssueLookup;
  path: string;
}

/**
 * What a tap does (BB-74 semantics): open the app, a deep link or a web page (in the browser or in the app). Links may
 * carry Liquid; a web page needs https once rendered.
 */
export function ActionEditor({ label, value, onChange, allowDismiss = false, readOnly = false, issues, path }: ActionEditorProps) {
  const uriIssues = issues?.(`${path}.uri`) ?? { errors: [], warnings: [] };
  return (
    <div className="push-action">
      <Field label={label}>
        <Select options={allowDismiss ? [...KINDS, DISMISS] : KINDS} value={value.kind} disabled={readOnly} onChange={(e) => onChange(switchKind(value, e.target.value as PushButtonAction['kind']))} />
      </Field>
      {value.kind === 'deep_link' && (
        <Field label={`${label}: deep link`} hint="An app link with a scheme, e.g. vcgmarkets://rewards; may use Liquid." error={uriIssues.errors[0]?.message}>
          <Input value={value.uri} placeholder="vcgmarkets://" disabled={readOnly} spellCheck={false} onChange={(e) => onChange({ ...value, uri: e.target.value })} />
        </Field>
      )}
      {value.kind === 'web_url' && (
        <>
          <Field label={`${label}: web URL`} hint="https:// only; may use Liquid." error={uriIssues.errors[0]?.message}>
            <Input value={value.uri} placeholder="https://" disabled={readOnly} spellCheck={false} onChange={(e) => onChange({ ...value, uri: e.target.value })} />
          </Field>
          <Field label={`${label}: open`}>
            <Select options={OPEN_IN} value={value.openIn ?? 'external'} disabled={readOnly} onChange={(e) => onChange({ ...value, openIn: e.target.value as 'external' | 'in_app' })} />
          </Field>
        </>
      )}
      <FieldIssueList issues={{ errors: uriIssues.errors.slice(1), warnings: uriIssues.warnings }} />
    </div>
  );
}
